import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";

const OverviewProfile = ({ data }) => {
  const navigate = useNavigate();

  // -------- STATS FROM API --------
  const stats = useMemo(
    () => [
      { label: "Applied", value: data?.applied_count || 0 },
      { label: "Shortlisted", value: data?.shortlisted_count || 0 },
      { label: "Interviewed", value: data?.interviewed_count || 0 },
      { label: "Hired", value: data?.hired_count || 0 },
    ],
    [data]
  );

  // -------- GENERAL DETAILS --------
  const details = useMemo(
    () => ({
      Title: data.title || "-",
      Department: data.department?.name || "-",
      Vacancies: data.no_of_vacancies || "-",
      "Employment Type": data.employment_type || "-",
      "Work Mode": data.work_mode || "-",
      "Opening Date": data.opening_date || "-",
      "Closing Date": data.closing_date || "-",
      Address: data.address || "-",
    }),
    [data]
  );

  // -------- REQUIREMENTS SECTION --------
  const requirements = useMemo(
    () => ({
      "Minimum Education": data.minimum_education || "-",
      "Preferred Education": data.preferred_education || "-",
      Experience: `${data.minimum_experience || 0} - ${
        data.maximum_experience || 0
      } Years`,
      "Key Skills": data.key_skills?.join(", ") || "-",
      "Salary Range": `${data.salary_min || 0} - ${data.salary_max || 0} ${
        data.currency?.toUpperCase() || ""
      } (${data.salary_type || "-"})`,
    }),
    [data]
  );

  // -------- JOB RESPONSIBILITIES & PERKS --------
  const responsibilities = data.job_responsibilities
    ? data.job_responsibilities.split("\n")
    : [];

  const perks = data.perks_benefits ? data.perks_benefits.split(",") : [];

  return (
    <div className="flex flex-col gap-2">
      {/* GRAPH + DETAILS */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
        {/* GRAPH SECTION */}
        <div className="p-4 bg-white rounded-lg shadow">
          <h3 className="text-lg font-semibold mb-4">Application Status</h3>

          {stats.map((item) => {
            const maxValue = Math.max(...stats.map((s) => s.value), 1); // Get max value, minimum 1 to avoid division by zero
            const percentage = maxValue > 0 ? (item.value / maxValue) * 100 : 0;
            
            return (
            <div key={item.label} className="mb-3">
              <div className="flex justify-between text-sm mb-1">
                <span>{item.label}</span>
                <span>{item.value}</span>
              </div>
              <div className="w-full bg-gray-200 h-3 rounded">
                <div
                  className="h-3 bg-blue-500 rounded"
                    style={{ width: `${percentage}%` }}
                ></div>
              </div>
            </div>
            );
          })}
        </div>

        {/* GENERAL DETAILS */}
        <div className="bg-white rounded-lg shadow text-theme">
          <h3 className="text-lg font-semibold border-b p-4">
            General Details
          </h3>
          <div className="flex flex-col gap-4 text-sm p-4">
            {Object.entries(details).map(([key, value]) => (
              <div key={key} className="grid grid-cols-2 gap-4">
                <span className="font-semibold">{key}:</span>
                <span>{value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* REQUIREMENTS + JOB DESCRIPTION */}
      <div className="flex flex-col gap-2">
        {/* REQUIREMENTS */}
        <div className="bg-white rounded-lg shadow text-theme">
          <h3 className="text-lg font-semibold border-b p-4">Requirements</h3>
          <div className="grid grid-cols-2 gap-y-4 text-sm p-4">
            {Object.entries(requirements).map(([key, value]) => (
              <div key={key} className="grid grid-cols-2">
                <span className="text-theme/60 font-semibold">{key}:</span>
                <span className="font-semibold">{value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* JOB DESCRIPTION */}
        <div className="bg-white rounded-lg shadow flex flex-col gap-4 text-theme">
          <h3 className="text-lg font-semibold border-b p-4">
            Job Description
          </h3>

          <div className="p-4 pt-0 flex flex-col gap-4">
            <div className="flex flex-row w-full">
              <h4 className="font-semibold w-1/4 text-theme/60">
                Job Responsibilities:
              </h4>
              <ul className="list-disc ml-6 text-sm font-semibold">
                {responsibilities.map((item, i) => (
                  <li key={i}>{item.trim()}</li>
                ))}
              </ul>
            </div>

            <div className="flex flex-row w-full">
              <h4 className="font-semibold w-1/4 text-theme/60">
                Perks & Benefits:
              </h4>
              <ul className="list-disc ml-6 text-sm font-semibold">
                {perks.map((item, i) => (
                  <li key={i}>{item.trim()}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OverviewProfile;
